import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';

import {
  getCertificatePermissionsQueryKey,
  useWithdrawCertificatePermission,
} from '../../../../../../../../hooks/api/certificates';
import useDialogContext from '../../../../../../../../hooks/dialog/useDialogContext';
import useNotificationContext from '../../../../../../../../hooks/notification/useNotificationContext';
import { ToastSeverity } from '../../../../../../../../shared/enums/toast-severity';
import { getWithdrawCertificatePermissionErrorMessage } from '../../../../../../../../shared/helpers/get-withdraw-certificate-permission-error-message';
import { ICertificatePermission } from '../../../../../../../../shared/interfaces/ICertificate';

type WithdrawCertificateButtonProps = {
  permissionId: number;
  certificateId?: string;
};

const WithdrawCertificateButton = ({
  permissionId,
  certificateId,
}: WithdrawCertificateButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const { openDialog } = useDialogContext();
  const { notify } = useNotificationContext();
  const queryClient = useQueryClient();

  // Optimistically drop the row so it disappears as soon as the withdrawal is confirmed.
  // The refetch in onSettled is the source of truth: it silently confirms the removal on
  // success, and brings the row back if the withdrawal failed.
  const onMutate = async ({ permissionId: withdrawnId }: { permissionId: number }) => {
    if (!certificateId) {
      return;
    }

    const queryKey = getCertificatePermissionsQueryKey(certificateId);

    // Prevent an in-flight refetch from resolving afterwards and resurrecting the row
    await queryClient.cancelQueries({ queryKey });

    queryClient.setQueryData<ICertificatePermission[]>(queryKey, (previousPermissions) => {
      return previousPermissions?.filter((permission) => {
        return permission.id !== withdrawnId;
      });
    });
  };

  const onError = (error: Error) => {
    notify({
      severity: ToastSeverity.ERROR,
      detail: getWithdrawCertificatePermissionErrorMessage(error, t),
      life: 3000,
    });
  };

  const onSettled = () => {
    if (!certificateId) {
      return;
    }

    queryClient.invalidateQueries({
      queryKey: getCertificatePermissionsQueryKey(certificateId),
    });
  };

  const { mutate: withdrawCertificatePermission } = useWithdrawCertificatePermission({
    onMutate,
    onError,
    onSettled,
  });

  const onConfirmWithdraw = () => {
    withdrawCertificatePermission({ permissionId });
  };

  return (
    <Button
      className='w-fit py-2 px-3 m-0'
      label={t('certificateDetailsPage.tabs.permissionsTab.buttons.withdraw')}
      onClick={() => {
        return openDialog('confirmation-dialog', {
          title: t('withdrawCertificateConfirmationDialog.title'),
          message: t('withdrawCertificateConfirmationDialog.content'),
          confirmButtonText: t('withdrawCertificateConfirmationDialog.buttons.withdraw'),
          cancelButtonText: t('withdrawCertificateConfirmationDialog.buttons.cancel'),
          onConfirm: onConfirmWithdraw,
        });
      }}
    />
  );
};

export default WithdrawCertificateButton;
